import Point from "@arcgis/core/geometry/Point";
import Polyline from "@arcgis/core/geometry/Polyline";
import { Data } from "plotly.js";
import { useContext, useEffect, useRef, useState } from "react";
import Plot from "../../genericComponents/Plot/Plot";
import { useDispatch, useSelector } from "react-redux";
import OverettIcon from "../../../assets/mountains.svg?react";
import { OverettContext } from "../../../Context";
import { setIsOverettChartOpen } from "../../../store/overettSlice";
import { StoreState } from "../../../store/rootReducer";
import { getMapView } from "../../../utils/arcgisUtils";
import { getOverettElevations, getOverettLinjeEndPoint } from "../../../utils/overettUtils";
import FloatingWindow from "../../genericComponents/FloatingWindow/FloatingWindow";
import Loader from "../../genericComponents/Loader/Loader";
import "./OverettChart.css";

const OverettChart = () => {
    const dispatch = useDispatch();
    const [altitudeData, setAltitudeData] = useState<number[][]>([]);
    const [maxAltitude, setMaxAltitude] = useState<number>(0);
    const [layout, setLayout] = useState<any>({});
    const [data, setData] = useState<Data[]>([]);
    const [overettUpper, setOverettUpper] = useState<Point | null>(null);
    const [overettLower, setOverettLower] = useState<Point | null>(null);
    const [overettUpperAltitude, setOverettUpperAltitude] = useState<number>(0);
    const [overettLowerAltitude, setOverettLowerAltitude] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const observationHeight = useSelector((state: StoreState) => state.overett.observationHeight);
    const upperOverettAltitudeAboveTerrain = useSelector(
        (state: StoreState) => state.overett.upperOverettAltitudeAboveTerrain
    );
    const lowerOverettAltitudeAboveTerrain = useSelector(
        (state: StoreState) => state.overett.lowerOverettAltitudeAboveTerrain
    );
    const overettAngle = useSelector((state: StoreState) => state.overett.overettAngle);

    const overettContext = useContext(OverettContext);
    const overettObjects = overettContext?.overettObjects.value;
    const overettLinje = overettContext?.overettLinjeRef.value;
    const observer = overettContext?.overettObserver.value;

    const overettChartRef = useRef(document.createElement("div"));

    let xdata: number[] = [];

    useEffect(() => {
        if (altitudeData.length === 0) return;
        setIsLoading(false);
    }, [altitudeData]);

    useEffect(() => {
        if (!overettUpper || !overettLower || !observer) return;
        const mapView = getMapView();
        if (!mapView?.map) return;

        const observerDistance = overettUpper.distance(observer);

        const resolution = 100;

        const queryLine = new Polyline({
            paths: [[[overettUpper.x, overettUpper.y]]],
            spatialReference: mapView.spatialReference,
        });

        let progress = 0;

        while (progress < observerDistance) {
            // Divide the line between the upper overett and observer into smaller segments
            const x = overettUpper.x + (observer.x - overettUpper.x) * (progress / observerDistance);
            const y = overettUpper.y + (observer.y - overettUpper.y) * (progress / observerDistance);
            xdata.push(progress);
            queryLine.paths[0].push([x, y]);
            progress += observerDistance / resolution;
        }

        mapView.map.ground.queryElevation(queryLine, { demResolution: 1 }).then((result) => {
            setAltitudeData((result.geometry as Polyline).paths[0].map((point, i) => [xdata[i], point[2]]));
            setMaxAltitude(Math.max(...(result.geometry as Polyline).paths[0].map((point) => point[2])));
        });
    }, [overettUpper, overettLower, observer]);

    useEffect(() => {
        if (!overettObjects) return;
        if (!overettObjects.upper || !overettObjects.lower) return;
        getOverettElevations(overettObjects.upper, overettObjects.lower).then((results) => {
            if (!results) return;
            const [upperOverett, lowerOverett, elevation1, elevation2] = results;

            setOverettUpper(upperOverett.geometry as Point);
            setOverettLower(lowerOverett.geometry as Point);
            setOverettUpperAltitude(elevation1);
            setOverettLowerAltitude(elevation2);
        });
    }, [overettObjects]);

    useEffect(() => {
        if (!overettLinje?.current || !overettUpper) return;

        const endPoint = getOverettLinjeEndPoint(overettLinje.current, overettUpper);
        if (!endPoint) return;

        overettContext?.overettObserver.set(endPoint);
    }, [overettLinje, overettUpper]);

    useEffect(() => {
        if (!overettUpper || !overettLower || !observer) return;

        const overettDistance = overettUpper.distance(overettLower);
        const observerDistance = overettUpper.distance(observer);

        const layout = {
            xaxis: {
                autorange: true,
                ticksuffix: " m",
            },
            yaxis: {
                autorange: true,
                ticksuffix: " m",
            },
            height: 300,
            width: 700,
            shapes: [
                {
                    type: "path" as const,
                    path: `M 0 ${overettUpperAltitude + upperOverettAltitudeAboveTerrain} L ${overettDistance} ${
                        overettLowerAltitude + lowerOverettAltitudeAboveTerrain
                    } L ${observerDistance} ${observationHeight} Z`,
                    fillcolor: "rgba(217, 242, 236, 0.8)",
                    line: {
                        color: "transparent",
                    },
                },
                {
                    type: "line" as const,
                    xref: "x" as const,
                    yref: "y" as const,
                    x0: 0,
                    y0: overettUpperAltitude + upperOverettAltitudeAboveTerrain,
                    x1: observerDistance,
                    y1: observationHeight,
                    line: {
                        color: "black",
                        width: 1,
                        dash: "dot" as const,
                    },
                },
                {
                    type: "line" as const,
                    xref: "x" as const,
                    yref: "y" as const,
                    x0: overettDistance,
                    y0: overettLowerAltitude + lowerOverettAltitudeAboveTerrain,
                    x1: observerDistance,
                    y1: observationHeight,
                    line: {
                        color: "black",
                        width: 1,
                        dash: "dot" as const,
                    },
                },
            ],
            annotations: [
                {
                    x: 0,
                    y: overettUpperAltitude + upperOverettAltitudeAboveTerrain,
                    xref: "x" as const,
                    yref: "y" as const,
                    text: "Øvre overett",
                    ax: 0,
                    ay: -20,
                    arrowsize: 1.1,
                    arrowhead: 2,
                },
                {
                    x: overettDistance,
                    y: overettLowerAltitude + lowerOverettAltitudeAboveTerrain,
                    xref: "x" as const,
                    yref: "y" as const,
                    text: "Nedre overett",
                    ax: 0,
                    ay: -20,
                    arrowsize: 1.1,
                    arrowhead: 2,
                },
                {
                    x: observerDistance,
                    y: observationHeight,
                    xref: "x" as const,
                    yref: "y" as const,
                    text: "Observatør",
                    ax: 0,
                    ay: -20,
                    arrowsize: 1.1,
                    arrowhead: 2,
                },
                {
                    x: observerDistance,
                    y: observationHeight,
                    xref: "x" as const,
                    yref: "y" as const,
                    ax: 0,
                    ay: -10,
                    arrowhead: 6,
                },
            ],
            margin: {
                t: 30,
                b: 40,
                l: 50,
                r: 50,
            },
        };

        const data = [
            {
                x: altitudeData.map((point) => point[0]),
                y: altitudeData.map((point) => point[1]),
                fill: "tozeroy" as const,
                fillcolor: "rgba(187, 232, 246, 0.8)",
                type: "scatter" as const,
                mode: "none" as const,
            } as Data,
        ];

        setLayout(layout);
        setData(data);
    }, [
        altitudeData,
        maxAltitude,
        overettUpper,
        overettLower,
        observer,
        overettUpperAltitude,
        overettLowerAltitude,
        observationHeight,
        upperOverettAltitudeAboveTerrain,
        lowerOverettAltitudeAboveTerrain,
        overettAngle,
    ]);

    const renderChart = () => {
        if (!overettUpper || !overettLower) {
            return <div>Mangler overettobjekter, kan ikke vise graf.</div>;
        } else if (isLoading) {
            return <Loader />;
        } else {
            return <Plot layout={layout} data={data} config={{ staticPlot: true }} className="overett-chart-plot" />;
        }
    };

    return (
        <FloatingWindow
            title="Høydeprofil"
            className="overett-chart-window"
            toolRef={overettChartRef}
            closeWindow={() => {
                dispatch(setIsOverettChartOpen(false));
            }}
            Icon={OverettIcon}
        >
            {renderChart()}
        </FloatingWindow>
    );
};

export default OverettChart;
